// New Season — the sensors of the seasons being edited, as Edit Field holds
// them (sensor objects, Tal Gil sensors linked to the farming unit's), plus
// the user's unsaved changes per season.
//
// A season the user has not touched reads straight from its detail; the
// first change stores a copy for that season. Changes are dropped when the
// selection changes (as the form values are) and after the season is saved.
const { useState, useEffect, useMemo, useCallback } = require('react');
const GeneralClasses = require('GeneralClasses');
const { sensorsForEditing, cloneSensors } = require('./sensorEditRules');

// The sensors a season is saved with when the user did not change them —
// what Edit Field would send for that season.
const sensorsOfDetail = (detail) =>
  sensorsForEditing(
    detail?.season?.sensors,
    detail?.farmingUnitSensors,
    GeneralClasses,
  );

const useSeasonSensors = (activeRows, details) => {
  const [edits, setEdits] = useState({});
  const seasonIdsKey = activeRows.map((row) => row.season.id).join('|');
  useEffect(() => {
    setEdits({});
  }, [seasonIdsKey]);

  // Keyed on the ids, not the rows array (a new array every render).
  const fromDetails = useMemo(
    () =>
      seasonIdsKey
        .split('|')
        .filter(Boolean)
        .reduce((bySeason, seasonId) => {
          const detail = details[seasonId];
          if (detail?.season) {
            bySeason[seasonId] = sensorsOfDetail(detail);
          }
          return bySeason;
        }, {}),
    [seasonIdsKey, details],
  );

  const sensorsBySeason = useMemo(
    () => ({ ...fromDetails, ...edits }),
    [fromDetails, edits],
  );

  // update(seasonId, sensors => sensors): gets a copy to change and return.
  const update = useCallback(
    (seasonId, change) =>
      setEdits((previous) => {
        const current = previous[seasonId] || fromDetails[seasonId] || {};
        return { ...previous, [seasonId]: change(cloneSensors(current)) };
      }),
    [fromDetails],
  );

  const clear = useCallback(
    (seasonId) =>
      setEdits((previous) => {
        const next = { ...previous };
        delete next[seasonId];
        return next;
      }),
    [],
  );

  // The user's sensors for a season, or undefined if untouched.
  const editedSensorsOf = useCallback((seasonId) => edits[seasonId], [edits]);

  return {
    sensorsBySeason,
    editedSeasonIds: Object.keys(edits),
    editedSensorsOf,
    isDirty: Object.keys(edits).length > 0,
    update,
    clear,
  };
};

module.exports = { useSeasonSensors, sensorsOfDetail };
